'use client'

import { HttpTypes } from "@medusajs/types"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { PawIcon, ArrowRightIcon } from './icons'

interface PetSelectFeaturedProductsProps {
  products: HttpTypes.StoreProduct[]
  region: HttpTypes.StoreRegion
}

export function PetSelectFeaturedProducts({ products, region }: PetSelectFeaturedProductsProps) {
  if (!products || products.length === 0) return null

  const formatPrice = (product: HttpTypes.StoreProduct) => {
    const variant = product.variants?.[0]
    const price = variant?.calculated_price
    if (!price) return null
    const amount = price.calculated_amount
    const currency = price.currency_code?.toUpperCase() || 'AUD'
    return `$${Number(amount).toFixed(2)} ${currency}`
  }

  return (
    <section className="py-14 md:py-18 bg-gray-50/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[#0B7A75] text-xs font-semibold tracking-widest uppercase mb-2">Curated Selection</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Featured Products
            </h2>
          </div>
          <LocalizedClientLink
            href="/store"
            className="hidden md:inline-flex items-center gap-2 text-[#0B7A75] hover:text-[#065A55] font-semibold text-sm transition-colors"
          >
            View All
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </LocalizedClientLink>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {products.slice(0, 8).map((product) => (
            <LocalizedClientLink
              key={product.id}
              href={`/products/${product.handle}`}
              className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-300"
            >
              <div className="relative aspect-square overflow-hidden bg-gray-50">
                {product.thumbnail ? (
                  <Image
                    src={product.thumbnail}
                    alt={product.title || ''}
                    fill
                    sizes="(max-width: 768px) 50vw, 25vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PawIcon className="w-10 h-10 text-gray-200" />
                  </div>
                )}
              </div>
              <div className="p-3 md:p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-[#0B7A75] transition-colors">
                  {product.title}
                </h3>
                {formatPrice(product) && (
                  <p className="text-[#0B7A75] font-bold text-sm">
                    {formatPrice(product)}
                  </p>
                )}
              </div>
            </LocalizedClientLink>
          ))}
        </div>

        <div className="mt-8 text-center md:hidden">
          <LocalizedClientLink
            href="/store"
            className="inline-flex items-center gap-2 bg-[#0B7A75] text-white font-semibold px-6 py-3 rounded-full text-sm hover:bg-[#065A55] transition-colors"
          >
            View All Products
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </LocalizedClientLink>
        </div>
      </div>
    </section>
  )
}
